import type { DesignTokens } from './designTokens';

/**
 * Per-user colour themes, picked from the avatar menu. A preset re-colours the
 * app chrome (header, sidebar tint, buttons) and the light surfaces
 * (page, cards, panels, grid header) for the one user who picked it.
 *
 * Light mode only: the dark palette keeps its own lifted colours, the same rule
 * `applyBrandColors` follows for the tenant's header and secondary.
 *
 * The footer is never part of a preset: it stays the company's colour from
 * Appearance for everyone.
 *
 * Deliberately NOT touched by any preset: document-type colours (--vt-*), the
 * positive/negative tints and the form controls. They carry meaning, so they
 * must read the same whatever the user picked.
 */

export type ThemePresetId =
    | 'default'
    | 'ivory'
    | 'slate'
    | 'graphite'
    | 'sky'
    | 'ocean'
    | 'forest'
    | 'lavender'
    | 'plum'
    | 'rose'
    | 'lilac'
    | 'powder'
    | 'sand'
    | 'amber'
    | 'sunflower'
    | 'burgundy';

export interface ThemePresetSurface {
    page: string;
    card: string;
    panel: string;
    border: string;
    gridHeader: string;
}

export interface ThemePreset {
    id: ThemePresetId;
    label: string;
    /** Brand colours this preset replaces. Absent keys keep the company's (Appearance). */
    color: Partial<Omit<DesignTokens['color'], 'footer' | 'footerText'>>;
    /** Absent on `default`: the shipped surfaces in base.scss / the palette apply. */
    surface?: ThemePresetSurface;
}

/** The shipped light surfaces, for previewing `default` in the picker. */
export const DEFAULT_SURFACE: ThemePresetSurface = {
    page: '#f4f5fd',
    card: '#ffffff',
    panel: '#f8fafc',
    border: '#e2e8f0',
    gridHeader: '#eff1f5'
};

export const THEME_PRESETS: ThemePreset[] = [
    // The company's own colours from Appearance — nothing overridden.
    { id: 'default', label: 'Company default', color: {} },
    {
        id: 'ivory',
        label: 'Ivory',
        color: {},
        surface: { page: '#f7f4ea', card: '#fffdf8', panel: '#fbf8f1', border: '#e7e0cf', gridHeader: '#f1ece0' }
    },
    {
        id: 'slate',
        label: 'Slate',
        color: { primary: '#5a6b80', secondary: '#3d4a5c', accent: '#f0a830', header: '#2b3340' },
        surface: { page: '#eff1f3', card: '#ffffff', panel: '#f7f8f9', border: '#dde1e6', gridHeader: '#eaedf0' }
    },
    {
        id: 'graphite',
        label: 'Graphite',
        color: { primary: '#5c6066', secondary: '#33363c', accent: '#ffb01e', header: '#1d1f23' },
        surface: { page: '#f2f3f4', card: '#ffffff', panel: '#f8f8f9', border: '#dedfe2', gridHeader: '#ebecee' }
    },
    {
        id: 'sky',
        label: 'Sky',
        color: { primary: '#3f7fbf', secondary: '#1f6aa8', accent: '#ffb01e', header: '#1e5a8e' },
        surface: { page: '#edf3f9', card: '#ffffff', panel: '#f6f9fd', border: '#d6e2ef', gridHeader: '#e4edf7' }
    },
    {
        id: 'ocean',
        label: 'Ocean',
        color: { primary: '#2f8796', secondary: '#136b79', accent: '#f2a541', header: '#0f4c5c' },
        surface: { page: '#edf4f5', card: '#ffffff', panel: '#f5fafb', border: '#d3e3e6', gridHeader: '#e2eef0' }
    },
    {
        id: 'forest',
        label: 'Forest',
        color: { primary: '#4a8460', secondary: '#2e6844', accent: '#d9a23a', header: '#24402f' },
        surface: { page: '#f0f4ef', card: '#ffffff', panel: '#f7faf6', border: '#d8e2d7', gridHeader: '#e6eee5' }
    },
    {
        id: 'lavender',
        label: 'Lavender',
        color: { primary: '#6c6cb0', secondary: '#52529a', accent: '#ffb01e', header: '#45457a' },
        surface: { page: '#f3f3f9', card: '#ffffff', panel: '#f9f9fd', border: '#dfdfee', gridHeader: '#eaeaf5' }
    },
    {
        id: 'plum',
        label: 'Plum',
        color: { primary: '#7a5a9a', secondary: '#5d4080', accent: '#e8a33d', header: '#3d2a4e' },
        surface: { page: '#f4f2f7', card: '#ffffff', panel: '#faf8fc', border: '#e2dbe9', gridHeader: '#ede8f3' }
    },
    {
        id: 'rose',
        label: 'Rose',
        color: { primary: '#b06a86', secondary: '#a3506f', accent: '#f2b84b', header: '#8c4a63' },
        surface: { page: '#faf2f5', card: '#ffffff', panel: '#fdf8fa', border: '#eedbe2', gridHeader: '#f6e8ee' }
    },
    {
        id: 'lilac',
        label: 'Lilac',
        color: { primary: '#8676bd', secondary: '#6e5ca8', accent: '#f2b84b', header: '#6a5a9c' },
        surface: { page: '#f6f4fb', card: '#ffffff', panel: '#fbfaff', border: '#e3ddf1', gridHeader: '#efebf8' }
    },
    {
        id: 'powder',
        label: 'Powder',
        color: { primary: '#a87894', secondary: '#8e5c7c', accent: '#e9b04a', header: '#7d5670' },
        surface: { page: '#fbf4f4', card: '#fffcfc', panel: '#fdf9f9', border: '#efdfe2', gridHeader: '#f7ecee' }
    },
    {
        id: 'sand',
        label: 'Sand',
        color: { primary: '#9a7a55', secondary: '#7a5934', accent: '#c9822b', header: '#4a3a2a' },
        surface: { page: '#f5f2ea', card: '#fffdf8', panel: '#faf7f0', border: '#e5ddcd', gridHeader: '#efe8da' }
    },
    // Yellow and orange tops. A bright yellow bar cannot carry the header's white
    // text and icons, so both use a deep tone and put the other colour in the accent.
    {
        id: 'amber',
        label: 'Amber',
        color: { primary: '#d0782e', secondary: '#b45309', accent: '#ffd54a', header: '#b45309' },
        surface: { page: '#fbf5ee', card: '#ffffff', panel: '#fdf9f4', border: '#efdfcc', gridHeader: '#f8ecdd' }
    },
    {
        id: 'sunflower',
        label: 'Sunflower',
        color: { primary: '#c99a1e', secondary: '#8a6a00', accent: '#ff8a3d', header: '#8a6a00' },
        surface: { page: '#fbf8ec', card: '#fffef9', panel: '#fdfbf2', border: '#ece3c4', gridHeader: '#f6f0d9' }
    },
    {
        id: 'burgundy',
        label: 'Burgundy',
        color: { primary: '#a54b5c', secondary: '#85303f', accent: '#e0a23c', header: '#5a1f2b' },
        surface: { page: '#f7f1f1', card: '#ffffff', panel: '#fcf8f8', border: '#ead9db', gridHeader: '#f3e7e8' }
    }
];

export function getThemePreset(id: string | null | undefined): ThemePreset {
    return THEME_PRESETS.find((p) => p.id === id) ?? THEME_PRESETS[0];
}

/**
 * The preset's CSS custom properties, scoped to light mode. `body[data-theme]`
 * out-specifies the `:root` defaults in base.scss and the token sink, so these
 * win without touching either. `--brand-*` and the nav tint are restated too:
 * CSS that cannot reach the MUI theme reads the brand through them.
 */
export function themePresetToCss(preset: ThemePreset, brand: DesignTokens['color']): string {
    if (preset.id === 'default') return '';
    const decls: string[] = [];
    if (preset.surface) {
        const s = preset.surface;
        decls.push(
            `--page-bg: ${s.page}`,
            `--card-bg: ${s.card}`,
            `--card-border: ${s.border}`,
            `--panel-bg: ${s.panel}`,
            `--panel-border: ${s.border}`,
            `--filter-panel-bg: ${s.panel}`,
            `--filter-panel-border: ${s.border}`,
            `--grid-header-bg: ${s.gridHeader}`
        );
    }
    decls.push(
        `--brand-primary: ${brand.primary}`,
        `--brand-accent: ${brand.accent}`,
        `--brand-secondary: ${brand.secondary}`,
        `--brand-header: ${brand.header}`,
        `--nav-active-bg: color-mix(in srgb, ${brand.accent} 12%, transparent)`,
        `--nav-hover-bg: color-mix(in srgb, ${brand.accent} 6%, transparent)`
    );
    return `body[data-theme="light"] {\n    ${decls.join(';\n    ')};\n}`;
}
